import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RestDaySelect } from '../components/employee/RestDaySelect';
import { useNavigate, useParams } from 'react-router-dom';
import { PersonalExtrasFields } from '../components/employee/PersonalExtrasFields';
import { StatutoryContributionsForm } from '../components/employee/StatutoryContributionsForm';
import { TaxReliefForm } from '../components/employee/TaxReliefForm';

export default function EmployeeEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reliefErrors, setReliefErrors] = useState(0);
  const [managerOptions, setManagerOptions] = useState([]);  // TC-EMP-032
  const [formData, setFormData] = useState({
    user_id: '',
    full_name: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    join_date: '',
    employment_type: 'full_time',
    rest_day: '',
    salary_basic: 0,
    salary_allowances: 0,
    // Iter129 — Structured allowance components. Previously hidden from
    // HR (only `salary_allowances` was editable), so payroll silently
    // mixed seed-data defaults (200/150/500) into payslips that HR could
    // not control. Reported by Ankur 19-Jun-2026.
    transport_allowance: 0,
    meal_allowance: 0,
    housing_allowance: 0,
    other_allowance: 0,
    notice_period_days: '', // Iter88p — per-employee override
    ic_number: '',
    nationality: 'malaysian',
    marital_status: 'single',
    spouse_working: false,
    number_of_children: 0,
    disabled_self: false,
    disabled_spouse: false,
    disabled_children: 0,
    epf_number: '',
    socso_number: '',
    bank_name: '',
    bank_account: '',
    manager_id: '',  // TC-EMP-032 Reporting Manager
    passport_number: '',
    passport_expiry: '',
    visa_reference: '',
    visa_expiry: '',
    religion: '',
    highest_qualification: {},
    statutory_settings: null,
    tax_reliefs: {},
    loan_outstanding_balance: '',
  });

  useEffect(() => {
    fetchEmployee();
    fetchManagers();
  }, [id]);

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      // Iter83 — Anyone active in the company can be a reporting manager
      // (e.g. Senior Engineer, Team Lead, Director). Previously this
      // filtered by `e.role` but Employee documents don't carry a `role`
      // field (role lives on the users table), so the dropdown was always
      // empty — reported by Ankur 03-Jun-2026. Now we just exclude:
      //  • self (no self-reporting)
      //  • inactive / terminated / pending_joining (cannot manage anyone)
      const allowable = (response.data || []).filter(
        (e) => e.employee_id !== id && e.status === 'active'
      );
      // Sort by full_name for predictable ordering
      allowable.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      setManagerOptions(allowable);
    } catch (e) {
      // non-fatal
    }
  };

  const fetchEmployee = async () => {
    try {
      const response = await axios.get(`${API}/employees/${id}`);
      const employee = response.data;
      setFormData({
        user_id: employee.user_id || '',
        full_name: employee.full_name || '',
        email: employee.email || '',
        phone: employee.phone || '',
        department: employee.department || '',
        position: employee.position || '',
        join_date: employee.join_date || new Date().toISOString().split('T')[0],
        employment_type: employee.employment_type || 'full_time',
        rest_day: employee.rest_day || '',
        salary_basic: employee.salary_basic || 0,
        salary_allowances: employee.salary_allowances || 0,
        transport_allowance: employee.transport_allowance || 0,
        meal_allowance: employee.meal_allowance || 0,
        housing_allowance: employee.housing_allowance || 0,
        other_allowance: employee.other_allowance || 0,
        notice_period_days: employee.notice_period_days ?? '',
        ic_number: employee.ic_number || '',
        nationality: employee.nationality || 'malaysian',
        marital_status: employee.marital_status || 'single',
        spouse_working: employee.spouse_working || false,
        number_of_children: employee.number_of_children || 0,
        disabled_self: employee.disabled_self || false,
        disabled_spouse: employee.disabled_spouse || false,
        disabled_children: employee.disabled_children || 0,
        epf_number: employee.epf_number || '',
        socso_number: employee.socso_number || '',
        bank_name: employee.bank_name || '',
        bank_account: employee.bank_account || '',
        manager_id: employee.manager_id || '',  // TC-EMP-032
        passport_number: employee.passport_number || '',
        passport_expiry: employee.passport_expiry || '',
        visa_reference: employee.visa_reference || '',
        visa_expiry: employee.visa_expiry || '',
        religion: employee.religion || '',
        highest_qualification: employee.highest_qualification || {},
        statutory_settings: employee.statutory_settings || null,
        tax_reliefs: employee.tax_reliefs || {},
        loan_outstanding_balance: employee.loan_outstanding_balance ?? '',
      });
    } catch (error) {
      toast.error('Failed to fetch employee details');
      navigate('/dashboard/employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation (TC-EMP-019..028, TC-EMP-030/033)
    if (reliefErrors > 0) {
      toast.error(`${reliefErrors} tax relief amount(s) exceed the LHDN limit. Reduce them to the maximum shown before saving.`);
      return;
    }
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (formData.email && !emailRegex.test(formData.email.trim())) {
      toast.error('Invalid email format');
      return;
    }
    if (formData.ic_number) {
      const icClean = formData.ic_number.replace(/[-\s]/g, '');
      if (/^0+$/.test(icClean)) {
        toast.error('Invalid IC number — cannot be all zeros');
        return;
      }
      if (formData.nationality === 'malaysian' && !/^\d{12}$/.test(icClean)) {
        toast.error('Invalid Malaysian IC. Must be 12 digits (YYMMDD-PB-XXXX)');
        return;
      }
      const yy = parseInt(icClean.slice(0, 2), 10);
      const mm = parseInt(icClean.slice(2, 4), 10);
      const dd = parseInt(icClean.slice(4, 6), 10);
      const fullYear = yy > (new Date().getFullYear() % 100) ? 1900 + yy : 2000 + yy;
      const probe = new Date(fullYear, mm - 1, dd);
      if (probe.getFullYear() !== fullYear || probe.getMonth() !== mm - 1 || probe.getDate() !== dd) {
        toast.error('Invalid IC — date in IC is not a real calendar date');
        return;
      }
    }
    if (formData.phone) {
      const phoneDigits = formData.phone.replace(/[\s\-()]/g, '');
      const phoneValid = /^\+?60\d{8,11}$/.test(phoneDigits) ||
                         /^0\d{8,10}$/.test(phoneDigits) ||
                         /^\+\d{8,15}$/.test(phoneDigits);
      if (!phoneValid) {
        toast.error('Invalid phone number. Use Malaysian format (e.g. +60123456789)');
        return;
      }
    }
    if (formData.join_date) {
      // TC-EMP-026: Future join dates are ALLOWED (offer letters / future starters)
      try { new Date(formData.join_date); } catch (_) {
        toast.error('Invalid join date');
        return;
      }
    }
    const salary = parseFloat(formData.salary_basic) || 0;
    if (salary < 0) {
      toast.error('Salary cannot be negative');
      return;
    }
    if (salary > 0 && salary < 1700) {
      toast.error('Basic salary is below Malaysia minimum wage (RM 1,700)');
      return;
    }

    setSaving(true);
    try {
      // Iter129 — Sum the 4 structured allowance buckets so the legacy
      // `salary_allowances` field stays consistent (used by older
      // reports / legacy callers). Payroll itself reads the structured
      // fields, so this is purely a derived display total.
      const transport = parseFloat(formData.transport_allowance) || 0;
      const meal = parseFloat(formData.meal_allowance) || 0;
      const housing = parseFloat(formData.housing_allowance) || 0;
      const other = parseFloat(formData.other_allowance) || 0;
      const totalAllowances = +(transport + meal + housing + other).toFixed(2);
      const payload = {
        ...formData,
        rest_day: formData.rest_day || null,
        salary_basic: parseFloat(formData.salary_basic) || 0,
        salary_allowances: totalAllowances,
        transport_allowance: transport,
        meal_allowance: meal,
        housing_allowance: housing,
        other_allowance: other,
        // Iter88p — empty string ⇒ null so backend falls back to company policy
        notice_period_days:
          formData.notice_period_days === '' || formData.notice_period_days === null
            ? null
            : (parseInt(formData.notice_period_days, 10) || 0),
        passport_number: formData.passport_number || null,
        passport_expiry: formData.passport_expiry || null,
        visa_reference: formData.visa_reference || null,
        visa_expiry: formData.visa_expiry || null,
        religion: formData.religion || null,
        highest_qualification: Object.keys(formData.highest_qualification || {}).length ? formData.highest_qualification : null,
        statutory_settings: formData.statutory_settings || null,
        tax_reliefs: formData.tax_reliefs || {},
        loan_outstanding_balance:
          formData.loan_outstanding_balance === '' || formData.loan_outstanding_balance === null
            ? null : (parseFloat(formData.loan_outstanding_balance) || 0),
      };
      
      await axios.put(`${API}/employees/${id}`, payload);
      toast.success('Employee updated successfully!');
      navigate('/dashboard/employees');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/employees')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Employees</span>
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Edit Employee</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">{formData.full_name}</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
        <div className="space-y-6">
          {/* TC-EMP-037..041: Profile Photo */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Profile Photo</h3>
            <EditEmployeePhotoBlock employeeId={id} employeeFullName={formData.full_name || 'Employee'} />
          </div>

          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Doe"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+60123456789"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>NRIC No.</Label>
                <Input
                  value={formData.ic_number}
                  onChange={(e) => setFormData({ ...formData, ic_number: e.target.value })}
                  placeholder="990101-01-1234"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Nationality</Label>
                <select
                  value={formData.nationality}
                  onChange={(e) => setFormData({ ...formData, nationality: e.target.value, statutory_settings: null })}
                  className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100"
                  data-testid="edit-employee-nationality"
                >
                  <option value="malaysian">Malaysian</option>
                  <option value="non_malaysian_permanent">Non-Malaysian (Permanent Resident)</option>
                  <option value="non_malaysian_temporary">Non-Malaysian (Temporary)</option>
                </select>
              </div>
              <PersonalExtrasFields form={formData} onChange={(patch) => setFormData((f) => ({ ...f, ...patch }))} />
            </div>
          </div>

          {/* Employment Information */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Employment Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="Engineering"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Position</Label>
                <Input
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  placeholder="Senior Developer"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Join Date *</Label>
                <Input
                  required
                  type="date"
                  value={formData.join_date}
                  onChange={(e) => setFormData({ ...formData, join_date: e.target.value })}
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Employment Type</Label>
                <select
                  value={formData.employment_type}
                  onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="full_time">Full Time</option>
                  <option value="part_time">Part Time</option>
                  <option value="contract">Contract</option>
                </select>
              </div>
              <RestDaySelect value={formData.rest_day} onChange={(v) => setFormData({ ...formData, rest_day: v })} testId="edit-rest-day-select" />
              {/* TC-EMP-032: Reporting Manager */}
              <div>
                <Label>Reporting Manager</Label>
                <select
                  data-testid="edit-manager-select"
                  value={formData.manager_id}
                  onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="">No Manager</option>
                  {managerOptions.map((mgr) => (
                    <option key={mgr.employee_id} value={mgr.employee_id}>
                      {mgr.full_name}{mgr.position ? ` — ${mgr.position}` : ''}{mgr.department ? ` · ${mgr.department}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Salary Information */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Salary Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Basic Salary (RM) *</Label>
                <Input
                  required
                  type="number"
                  step="0.01"
                  value={formData.salary_basic}
                  onChange={(e) => setFormData({ ...formData, salary_basic: e.target.value })}
                  placeholder="5000.00"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Transport Allowance (RM)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="edit-employee-transport-allowance"
                  value={formData.transport_allowance}
                  onChange={(e) => setFormData({ ...formData, transport_allowance: e.target.value })}
                  placeholder="0.00"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Meal Allowance (RM)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="edit-employee-meal-allowance"
                  value={formData.meal_allowance}
                  onChange={(e) => setFormData({ ...formData, meal_allowance: e.target.value })}
                  placeholder="0.00"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Housing Allowance (RM)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="edit-employee-housing-allowance"
                  value={formData.housing_allowance}
                  onChange={(e) => setFormData({ ...formData, housing_allowance: e.target.value })}
                  placeholder="0.00"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Other Allowance (RM)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="edit-employee-other-allowance"
                  value={formData.other_allowance}
                  onChange={(e) => setFormData({ ...formData, other_allowance: e.target.value })}
                  placeholder="0.00"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Total Allowances: <strong>
                    RM {(
                      (parseFloat(formData.transport_allowance) || 0)
                      + (parseFloat(formData.meal_allowance) || 0)
                      + (parseFloat(formData.housing_allowance) || 0)
                      + (parseFloat(formData.other_allowance) || 0)
                    ).toFixed(2)}
                  </strong>
                </p>
              </div>
              <div>
                <Label>Notice Period (days)</Label>
                <Input
                  type="number"
                  min="0"
                  data-testid="edit-employee-notice-period-input"
                  value={formData.notice_period_days}
                  onChange={(e) => setFormData({ ...formData, notice_period_days: e.target.value })}
                  placeholder="Leave blank to use company policy"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Overrides company HR policy for this employee only. Useful for probation,
                  senior staff, or contract-specific terms. Leave blank to use the default.
                </p>
              </div>
              <div>
                <Label>Outstanding Staff Loan Balance (RM)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  data-testid="edit-employee-loan-balance-input"
                  value={formData.loan_outstanding_balance}
                  onChange={(e) => setFormData({ ...formData, loan_outstanding_balance: e.target.value })}
                  placeholder="0.00"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
                <p className="text-xs text-slate-500 mt-1">Deducted from the Final Settlement when the employee leaves.</p>
              </div>
            </div>
          </div>

          {/* Tax Relief Information */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Tax Relief Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Marital Status</Label>
                <select
                  value={formData.marital_status}
                  onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                </select>
              </div>
              {formData.marital_status === 'married' && (
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="spouse_working"
                    checked={formData.spouse_working}
                    onChange={(e) => setFormData({ ...formData, spouse_working: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
                  />
                  <Label htmlFor="spouse_working" className="mb-0">Spouse Working</Label>
                </div>
              )}
              <div>
                <Label>Number of Children</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.number_of_children}
                  onChange={(e) => setFormData({ ...formData, number_of_children: parseInt(e.target.value) || 0 })}
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Disabled Children</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.disabled_children}
                  onChange={(e) => setFormData({ ...formData, disabled_children: parseInt(e.target.value) || 0 })}
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <input
                  type="checkbox"
                  id="disabled_self"
                  checked={formData.disabled_self}
                  onChange={(e) => setFormData({ ...formData, disabled_self: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
                />
                <Label htmlFor="disabled_self" className="mb-0">Employee is Disabled</Label>
              </div>
              {formData.marital_status === 'married' && (
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="disabled_spouse"
                    checked={formData.disabled_spouse}
                    onChange={(e) => setFormData({ ...formData, disabled_spouse: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
                  />
                  <Label htmlFor="disabled_spouse" className="mb-0">Spouse is Disabled</Label>
                </div>
              )}
            </div>
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">LHDN TP1 Relief Declarations</h4>
              <TaxReliefForm value={formData.tax_reliefs} onChange={(tr) => setFormData((f) => ({ ...f, tax_reliefs: tr }))} onErrorsChange={setReliefErrors} maritalStatus={formData.marital_status} childrenCount={Number(formData.number_of_children || 0) + Number(formData.disabled_children || 0)} />
            </div>
          </div>

          {/* Statutory Contributions */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Statutory Contributions</h3>
            <StatutoryContributionsForm
              value={formData.statutory_settings}
              onChange={(ss) => setFormData((f) => ({ ...f, statutory_settings: ss }))}
              nationality={formData.nationality}
              icNumber={formData.ic_number}
              wages={Number(formData.salary_basic || 0) + Number(formData.transport_allowance || 0) + Number(formData.meal_allowance || 0) + Number(formData.housing_allowance || 0) + Number(formData.other_allowance || 0)}
            />
          </div>

          {/* Statutory Information */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Statutory Numbers</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>EPF Number</Label>
                <Input
                  value={formData.epf_number}
                  onChange={(e) => setFormData({ ...formData, epf_number: e.target.value })}
                  placeholder="EPF123456"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>SOCSO Number</Label>
                <Input
                  value={formData.socso_number}
                  onChange={(e) => setFormData({ ...formData, socso_number: e.target.value })}
                  placeholder="SOCSO123456"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Bank Name</Label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="Maybank"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              <div>
                <Label>Bank Account</Label>
                <Input
                  value={formData.bank_account}
                  onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                  placeholder="123456789012"
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6 flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard/employees')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="edit-employee-save-btn"
              className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600"
              disabled={saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

// TC-EMP-037..041: Employee profile photo upload / preview / remove on Edit page
function EditEmployeePhotoBlock({ employeeId, employeeFullName }) {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = React.useRef(null);

  const refresh = async () => {
    try {
      const meta = await axios.get(`${API}/employees/${employeeId}`);
      if (meta.data?.profile_photo) {
        setHasPhoto(true);
        const token = localStorage.getItem('hrms_token');
        const res = await fetch(`${API}/employees/${employeeId}/photo/file`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const blob = await res.blob();
          setPhotoUrl(URL.createObjectURL(blob));
        }
      } else {
        setHasPhoto(false);
        setPhotoUrl(null);
      }
    } catch (_) { /* ignore */ }
  };

  useEffect(() => { refresh(); }, [employeeId]);

  const handleFile = async (file) => {
    setError('');
    if (!file) return;
    const allowed = ['image/jpeg','image/jpg','image/png','image/gif','image/webp'];
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!allowed.includes(file.type) && !['jpg','jpeg','png','gif','webp'].includes(ext)) {
      setError('Invalid file type. Only JPG, PNG, GIF, WEBP allowed.');
      toast.error('Invalid file type');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large (max 5MB).');
      toast.error('File too large (max 5MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/employees/${employeeId}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Profile photo updated');
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Upload failed');
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setUploading(true);
    try {
      await axios.delete(`${API}/employees/${employeeId}/photo`);
      toast.success('Photo removed');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-start gap-4" data-testid="edit-employee-photo-block">
      <div className="h-24 w-24 rounded-full overflow-hidden bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0 ring-2 ring-white dark:ring-slate-700 shadow-md">
        {photoUrl ? (
          <img src={photoUrl} alt={employeeFullName} className="h-full w-full object-cover" data-testid="edit-employee-photo-img" />
        ) : (
          <span className="text-white font-bold text-3xl">{(employeeFullName || 'E').charAt(0)}</span>
        )}
      </div>
      <div className="flex flex-col gap-2 max-w-[260px]">
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.webp,image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="edit-employee-photo-input"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid="edit-employee-photo-upload-btn"
        >
          {uploading ? 'Uploading...' : hasPhoto ? 'Change Photo' : 'Upload Photo'}
        </Button>
        {hasPhoto && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={uploading}
            onClick={handleRemove}
            className="text-red-600 hover:text-red-700"
            data-testid="edit-employee-photo-remove-btn"
          >
            Remove Photo
          </Button>
        )}
        <p className="text-xs text-slate-500">Max 5MB. JPG, PNG, GIF, WEBP.</p>
        {error && <p className="text-xs text-red-600" data-testid="edit-employee-photo-error">{error}</p>}
      </div>
    </div>
  );
}

